import { Component, Input, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe, AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';

/**
 * Parameters for Liquidation Quantity UMP Cell Renderer
 */
export interface LiquidationQuantityUMPCellRendererParams {
  UMPActive$: Observable<boolean>;
  quantityField: string;      // e.g., 'totalLoaded', 'totalDelivered', 'totalReturnedBE'
  baseUOMField: string;        // e.g., 'baseUnitOfMeasure'
  parallelUOMField: string;    // e.g., 'pararellUnitOfMeasure' (note: typo in interface)
}

/**
 * Structure of quantity data in liquidation details
 */
interface LiquidationQuantity {
  base: number;
  pararell: number;  // Note: Typo in API/interface, should be 'parallel'
}

/**
 * Structure of unit of measure in liquidation details
 */
interface LiquidationUnitOfMeasure {
  name: string;
  // ... other properties
}

/**
 * Liquidation Quantity UMP Cell Renderer
 *
 * Dynamically displays quantity information with:
 * - UMB (Base Unit of Measure) - Always visible
 * - UMP (Parallel Unit of Measure) - Conditionally visible based on UMPActive$ Observable
 *
 * This renderer is generic and can be used for any quantity field:
 * - totalLoaded
 * - totalDelivered
 * - totalReturnedBE
 * - totalReturnedME
 * - totalMissing
 *
 * The quantityField, baseUOMField, and parallelUOMField are passed via cellRendererParams,
 * allowing dynamic field access.
 *
 * @example
 * ```typescript
 * {
 *   colId: 'loaded',
 *   headerName: 'Cargado',
 *   cellRenderer: LiquidationQuantityUMPCellRendererComponent,
 *   cellRendererParams: {
 *     UMPActive$: this.UMPActive$,
 *     quantityField: 'totalLoaded',
 *     baseUOMField: 'baseUnitOfMeasure',
 *     parallelUOMField: 'pararellUnitOfMeasure'
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-liquidation-quantity-ump-cell-renderer',
  standalone: true,
  imports: [CommonModule, AsyncPipe, DecimalPipe],
  template: `
    <div class="text-sm">
      <!-- UMB (Always Visible) -->
      <div class="mb-1">
        <strong class="text-xs">UMB:</strong>
        <span>{{ baseQuantity | number: '1.2-3' }}</span>
        <span class="text-gray-500 dark:text-gray-400 text-xs ml-1">({{ baseUOM }})</span>
      </div>

      <!-- UMP (Conditional based on UMPActive$ Observable) -->
      <div *ngIf="(customParams?.UMPActive$ | async)">
        <strong class="text-xs">UMP:</strong>
        <ng-container *ngIf="parallelUOM; else noUMP">
          <span>{{ parallelQuantity | number: '1.2-3' }}</span>
          <span class="text-gray-500 dark:text-gray-400 text-xs ml-1">({{ parallelUOM }})</span>
        </ng-container>
        <ng-template #noUMP>
          <span class="text-gray-500 dark:text-gray-400">-</span>
        </ng-template>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiquidationQuantityUMPCellRendererComponent implements CellRenderer<any>, OnInit {
  @Input() params!: CellRendererParams<any>;

  get customParams(): LiquidationQuantityUMPCellRendererParams | undefined {
    return this.params?.colDef?.cellRendererParams as LiquidationQuantityUMPCellRendererParams;
  }

  /**
   * Gets the quantity data object from the row data using dynamic field access
   */
  get quantityData(): LiquidationQuantity | undefined {
    const field = this.customParams?.quantityField;
    if (!field || !this.params?.data) return undefined;
    return this.params.data[field] as LiquidationQuantity;
  }

  /**
   * Gets the base quantity value
   */
  get baseQuantity(): number {
    return this.quantityData?.base ?? 0;
  }

  /**
   * Gets the parallel quantity value
   * Note: Uses 'pararell' (typo in interface) instead of 'parallel'
   */
  get parallelQuantity(): number {
    return this.quantityData?.pararell ?? 0;
  }

  /**
   * Gets the base unit of measure name using dynamic field access
   */
  get baseUOM(): string {
    const field = this.customParams?.baseUOMField;
    if (!field || !this.params?.data) return '-';
    const uom = this.params.data[field] as LiquidationUnitOfMeasure;
    return uom?.name || '-';
  }

  /**
   * Gets the parallel unit of measure name using dynamic field access
   * Note: Field name is 'pararellUnitOfMeasure' (typo in interface)
   */
  get parallelUOM(): string {
    const field = this.customParams?.parallelUOMField;
    if (!field || !this.params?.data) return '';
    const uom = this.params.data[field] as LiquidationUnitOfMeasure;
    return uom?.name || '';
  }

  ngOnInit(): void {
    // Validation logging for development/debugging
    if (!this.customParams?.UMPActive$) {
      console.warn('[LiquidationQuantityUMPCellRenderer] UMPActive$ Observable not provided in cellRendererParams');
    }
    if (!this.customParams?.quantityField) {
      console.warn('[LiquidationQuantityUMPCellRenderer] quantityField not provided in cellRendererParams');
    }
  }
}
